import { useState, Dispatch, SetStateAction, useEffect } from 'react';
import './AddPrompt.css';
import backIcon from '../../assets/icon/backIcon.svg';
import addPromptIcon from '../../assets/icon/addPromptIcon.svg';
import resetIcon from '../../assets/icon/resetIcon.svg';
import tickIcon from '../../assets/icon/tickIcon.svg';
import deleteIcon from '../../assets/icon/deleteIcon1.svg';
import { useAzureData } from '../../context/AzureContext';
import {
  AddPromptAPI,
  EditPromptAPI,
  fetchMyPromptDetails,
  fetchPromptLibrarySuggestion
} from '../../service/apiServices';
import { usePromptLibraryContext } from '../../context/PromptLibraryContext';
import { UnileverButton } from '../ButtonComponents';
import { useNavigate, useLocation } from 'react-router-dom';
import { BlueDropDownComponent, SearchableDropDownComponent, ToolTipFunction } from '../inputField';
import DeletePopUp from '../DeletePopUp';
import { ReactComponent as I_icon } from '../../assets/icon/i_icon.svg';
import { usePromptPlaygroundContext } from '../../context/PromptPlayGroundContext';
import { usePromptContext } from '../../context/PromptContextType';

interface Variable {
  id: string;
  name: string;
  defaultValue: string;
  description: string;
}

interface RoleVariable {
  id: string;
  role: string;
  message: string;
}

interface PromptState {
  selectedApplication: string;
  promptName: string;
  promptVersion: number;
  llmTestedWith: string;
  promptDescription: string;
  systemPrompt: string;
  usageExample: string;
  is_prompt_type_private: boolean;
  is_interaction_type_single: boolean;
  businessGroup: string;
  promptId?: string;
  totalVersions?: number;
}

const ToggleSwitch = ({
  value,
  onToggle,
  labels
}: {
  value: boolean;
  onToggle: () => void;
  labels: [string, string];
}) => (
  <div className="addPromptToggle">
    <div className="toggle-container">
      <span className={!value ? 'label active' : 'label'}>{labels[0]}</span>
      <div className={`switch ${value ? 'public' : 'private'}`} onClick={onToggle}>
        <div className="slider-circle" />
      </div>
      <span className={value ? 'label active' : 'label'}>{labels[1]}</span>
    </div>
  </div>
);

const RoleField = ({
  role,
  onChange,
  onRemove,
  canRemove
}: {
  role: RoleVariable;
  onChange: (id: string, field: keyof RoleVariable, value: string) => void;
  onRemove: (id: string) => void;
  canRemove: boolean;
}) => (
  <div className="mb-3">
    <div className="card-body p-3">
      <div className="row g-3">
        <div className="col-md-4">
          <ToolTipFunction
            toolTipText={
              'Specify whether the AI should act as Reasoning agent, Knowledge retriever , Task executor or response critic etc.'
            }>
            <label className="form-label fw-medium">Role</label> <I_icon />
          </ToolTipFunction>
          <input
            type="text"
            className="form-control"
            placeholder="Enter Role"
            value={role.role}
            onChange={(e) => onChange(role.id, 'role', e.target.value)}
          />
        </div>
        <div className="col-md-8">
          <label className="form-label fw-medium">Message</label>
          <div className="d-flex gap-2">
            <input
              type="text"
              className="form-control"
              placeholder="Write your prompt here (use {{variable}} for placeholders)"
              value={role.message}
              onChange={(e) => onChange(role.id, 'message', e.target.value)}
            />
            {canRemove && (
              <button
                type="button"
                onClick={() => onRemove(role.id)}
                className="btn btn-sm btnDelete"
                style={{ minWidth: '40px' }}>
                <img src={deleteIcon} alt="delete" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const VariableField = ({
  variable,
  onChange,
  onRemove,
  canRemove
}: {
  variable: Variable;
  onChange: (id: string, field: keyof Variable, value: string) => void;
  onRemove: (id: string) => void;
  canRemove: boolean;
}) => (
  <div className="mb-3">
    <div className="card-body p-3">
      <div className="row g-3">
        <div className="col-md-4">
          <label className="form-label fw-medium">Variable Name</label>

          <input
            type="text"
            className="form-control"
            placeholder="Enter variable Name"
            value={variable.name}
            onChange={(e) => onChange(variable.id, 'name', e.target.value)}
          />
        </div>
        <div className="col-md-4">
          <label className="form-label fw-medium">Default Value</label>
          <input
            type="text"
            className="form-control"
            placeholder="Enter Default Value"
            value={variable.defaultValue}
            onChange={(e) => onChange(variable.id, 'defaultValue', e.target.value)}
          />
        </div>
        <div className="col-md-4">
          <label className="form-label fw-medium">Description</label>
          <div className="d-flex gap-2">
            <input
              type="text"
              className="form-control"
              placeholder="Description"
              value={variable.description}
              onChange={(e) => onChange(variable.id, 'description', e.target.value)}
            />
            {canRemove && (
              <button
                type="button"
                onClick={() => onRemove(variable.id)}
                className="btn btn-sm btnDelete"
                style={{ minWidth: '40px' }}>
                <img src={deleteIcon} alt="delete" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const initialPromptState: PromptState = {
  selectedApplication: '',
  promptName: '',
  promptVersion: 1,
  llmTestedWith: '',
  promptDescription: '',
  systemPrompt: '',
  usageExample: '',
  is_prompt_type_private: false,
  is_interaction_type_single: false,
  businessGroup: '',
  totalVersions: 1
};

const initialVariables: Variable[] = [{ id: '1', name: '', defaultValue: '', description: '' }];

const initialRoles: RoleVariable[] = [
  { id: '1', role: '', message: '' },
  { id: '2', role: '', message: '' }
];

interface AddPromptProps {
  setIsAddPrompt: Dispatch<SetStateAction<boolean>>;
  setAllPromptStatus: Dispatch<SetStateAction<boolean>>;
}

const AddPrompt = () => {
  const [addPromptData, setAddPromptData] = useState({
    prompt: {
      selectedApplication: '',
      promptName: '',
      promptVersion: 1,
      llmTestedWith: '',
      promptDescription: '',
      systemPrompt: '',
      usageExample: '',
      is_prompt_type_private: true,
      is_interaction_type_single: true,
      businessGroup: '',
      totalVersions: 1
    },
    roles: [
      { id: '1', role: '', message: '' },
      { id: '2', role: '', message: '' }
    ],
    variables: [{ id: '1', name: '', defaultValue: '', description: '' }]
  });

  const [prompt, setPrompt] = useState<PromptState>(addPromptData?.prompt);
  const [variables, setVariables] = useState<Variable[]>(addPromptData?.variables);
  const [roles, setRoles] = useState<RoleVariable[]>(addPromptData?.roles);
  const { apiToken } = useAzureData();
  const navigate = useNavigate();

  const handleChange = (field: keyof PromptState, value: string | boolean) => {
    setPrompt((prev) => ({ ...prev, [field]: value }));
  };

  const updateVariable = (id: string, field: keyof Variable, value: string) => {
    setVariables((prev) => prev.map((v) => (v.id === id ? { ...v, [field]: value } : v)));
  };

  const updateRole = (id: string, field: keyof RoleVariable, value: string) => {
    setRoles((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const { businessCategories, promptNameCheckFunction, businessCategoryFunction } =
    usePromptContext();

  const handleReset = () => {
    setPrompt(initialPromptState);
    setVariables(initialVariables);
    setRoles(initialRoles);
  };

  const [nameError, setNameError] = useState(false);

  const { getPromptlibraryData, allPromptData, myPromptData, isLoading, error } =
    usePromptLibraryContext();
  const [isDataLoading, setIsDataLoading] = useState(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const promptId = queryParams.get('promptId');
  const [selectedVersion, setSelectedVersion] = useState(0);
  const [versionsData, setVersionData] = useState([]);
  const [newVersion, setNewVersion] = useState(0);
  const { modelDataFunction, modelData } = usePromptPlaygroundContext();
  useEffect(() => {
    modelDataFunction();
    businessCategoryFunction();
    return () => {
      setNameError(false);
    };
  }, []);

  const [ModelOptions, setModeloptions] = useState([{ title: '', id: '' }]);

  useEffect(() => {
    // for selecting first available version for the view
    const temp_ = parseInt(versionsData?.[0]?.version);
    if (temp_) {
      setSelectedVersion(temp_ || 1);
    }
  }, [versionsData]);

  useEffect(() => {
    const values_2 = modelData?.data?.endpoints;
    if (Array.isArray(values_2)) {
      const temp = values_2?.map((item) => {
        return { title: item?.endpoint_name, id: item?.endpoint_name };
      });

      setModeloptions(temp || []);
    }
  }, [modelData]);

  const [fetchedData, setFetchedData] = useState();

  useEffect(() => {
    if (promptId) {
      try {
        fetchMyPromptDetails(promptId, apiToken)?.then((data) => {
          const tempVersions: { version: string; creation_date: string }[] = [];

          data?.promptsData?.map((item) =>
            tempVersions.push({
              version: item?.prompt?.promptVersion,
              creation_date: item?.prompt?.creation_date
            })
          );
          setVersionData(tempVersions);
          setFetchedData(data);
        });
      } catch (error) {
        console.log(error);
        alert(error);
      }
    }
  }, [promptId]);

  useEffect(() => {
    const data = fetchedData;
    const temp = data?.promptsData?.filter(
      (item) => item?.prompt?.promptVersion == selectedVersion
    )?.[0];

    const {
      promptName = '',
      llmTestedWith = '',
      promptVersion = 0,
      promptDescription = '',
      systemPrompt = '',
      is_interaction_type_single = true,
      is_prompt_type_private = true,
      businessGroup
    } = temp?.prompt || {};

    const { roles = [{ message: '', role: '' }], variables = [] } = temp || {};

    setNewVersion(
      parseInt(data?.promptsData[data?.promptsData?.length - 1]?.prompt?.promptVersion) + 1
    );

    setAddPromptData({
      prompt: {
        selectedApplication: '',
        promptName: promptName,
        totalVersions: temp?.prompt?.totalVersions,
        promptVersion: parseInt(promptVersion),
        llmTestedWith: llmTestedWith,
        promptDescription: promptDescription,
        systemPrompt: systemPrompt,
        usageExample: temp?.prompt?.prompt?.map((item) => item.text)?.join('\n'),
        is_prompt_type_private: is_prompt_type_private,
        is_interaction_type_single: is_interaction_type_single,
        businessGroup: businessGroup
      },

      roles: temp?.prompt?.is_interaction_type_single
        ? initialRoles
        : roles?.map((item, index) => {
            return { ...item, id: index };
          }),
      variables:
        variables?.map(
          (
            item: { VariableName: string; DefaultValue: string; Description: string },
            index: { index: number }
          ) => {
            return {
              id: index,
              name: item?.VariableName,
              defaultValue: item?.DefaultValue,
              description: item?.Description
            };
          }
        ) || []
    });
  }, [fetchedData, selectedVersion]);

  useEffect(() => {
    // this set the values in edit mode
    if (addPromptData?.prompt?.promptName) {
      setPrompt(addPromptData?.prompt);
      setRoles(addPromptData?.roles);
      setVariables(addPromptData?.variables);
      setManualSystemPrompt(true);
    }
  }, [addPromptData]);

  const [editPopUp, setEditPopUp] = useState(false);

  const handleEdit = () => {
    setEditPopUp(true);
  };

  const ToolTipLocalFunction = (toolTipData = <></>, mainData = <></>) => {
    return (
      <ToolTipFunction toolTipText={<span>{toolTipData}</span>}>
        <label className="form-label  mb-0">
          {mainData}
          <I_icon />
        </label>
      </ToolTipFunction>
    );
  };

  const handleSubmit = async (rdioBtn?: any) => {
    if (!promptId && prompt?.promptName) {
      const temp = await promptNameCheckFunction(prompt?.promptName);
      if (temp?.exists) {
        setNameError(true);
        return;
      } else {
        setNameError(false);
      }
    }

    const prompt1 = {
      ...prompt,
      usageExample: prompt?.is_interaction_type_single ? prompt?.usageExample : ''
    };
    const roles1 = prompt?.is_interaction_type_single ? [] : roles;

    const payload = {
      prompt: prompt1,
      roles: roles1,
      variables
    };
    setIsDataLoading(true);

    try {
      var temp;

      if (promptId) {
        var version = selectedVersion;
        var isNew = false;

        if (rdioBtn[0]?.checked) {
          // version = selectedVersion;
          isNew = false;
        } else {
          isNew = true;
          // version = prompt?.promptVersion + 1;
        }

        temp = await EditPromptAPI(payload, promptId, version, isNew, apiToken);
      } else {
        // console.log(payload, 'ppaaylooad 22');
        temp = await AddPromptAPI(payload, apiToken);
      }

      if (temp?.prompt?.promptName) {
        getPromptlibraryData();
        navigate(`/promptLibrary?is_prompt_type_private=${temp?.prompt?.is_prompt_type_private}`);
      }
    } catch (error) {
      console.error('Error while adding prompt:', error);

      alert(error);
    } finally {
      setIsDataLoading(false);
    }
  };
  const isButtonActive = () => {
    const isRoleInactive = roles?.some((item) => item?.role === '' || item.message === '');
    const isActive =
      !!prompt?.promptDescription &&
      !!prompt?.systemPrompt &&
      !!prompt?.promptName &&
      !!prompt?.businessGroup &&
      !!prompt?.llmTestedWith;

    if (prompt?.is_interaction_type_single) {
      return isActive && !!prompt?.usageExample;
    }
    if (!prompt?.is_interaction_type_single) {
      return isActive && !isRoleInactive;
    }
    return isActive;
  };

  // fetchingDetails from AI for population
  const [isSuggestionLoading, setSuggestionLoading] = useState(false);
  const [manualSystemPrompt, setManualSystemPrompt] = useState(false);
  useEffect(() => {
    // Only run if promptDescription exists
    if (!prompt?.promptDescription) return;
    if (manualSystemPrompt) {
      return;
    }
    const handler = setTimeout(() => {
      try {
        if (!!prompt?.promptDescription && !!prompt?.businessGroup && !manualSystemPrompt) {
          setSuggestionLoading(true);
          fetchPromptLibrarySuggestion(
            {
              description: prompt?.promptDescription,
              is_interaction_type_single: prompt?.is_interaction_type_single,
              businessGroup: prompt?.businessGroup
            },
            apiToken
          )?.then((data) => {
            !manualSystemPrompt &&
              setPrompt({ ...prompt, systemPrompt: data?.prompt?.systemPrompt });
            setSuggestionLoading(false);
          });
        }
      } catch (error) {
        console.log(error);
        alert(error);
      } finally {
      }
    }, 1000);

    return () => clearTimeout(handler);
  }, [prompt?.promptDescription, prompt?.businessGroup]);

  return (
    <div style={{ padding: 50 }}>
      <DeletePopUp
        popUpType="editPrompt"
        handleClose={() => setEditPopUp(false)}
        open={editPopUp}
        headerName="Save Prompt"
        description1="Select where to save this update:."
        handleEdit={(a) => handleSubmit(a)}
        versionDetail={{
          selected: selectedVersion,
          total: prompt?.totalVersions,
          newVersion: newVersion
        }}
      />
      <div className="addPrompt_wrapper">
        <div className="mb-4">
          <button
            onClick={() => navigate('/promptLibrary')}
            className="btn btn-link text-muted p-0 mb-3 text-decoration-none back_btn">
            <img src={backIcon} alt="back" height="14px" />
            Back
          </button>
          <div className="d-flex  align-items-center  justify-content-between">
            <div>
              <div className="d-flex align-items-center mb-2">
                <img src={addPromptIcon} alt="add" className="me-2" />
                <h1 className="h4 mb-0 addPrompt_heading">{promptId ? 'Edit' : 'Add'} Prompt</h1>
              </div>

              <p className="text-muted small mb-0 addPrompt_subheading">
                Edit key configuration details of your app to ensure proper identification,
                discovery, and integration within the platform.
              </p>
            </div>
            {promptId && (
              <BlueDropDownComponent
                versionsData={versionsData}
                // options={prompt?.totalVersions}
                selectedVersion={selectedVersion}
                handleSelection={(a, b) => setSelectedVersion(b)}
              />
            )}
          </div>
        </div>

        <div className="">
          <div className="">
            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <label className="form-label">
                  Prompt Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={prompt.promptName}
                  onChange={(e) => handleChange('promptName', e.target.value)}
                  className="form-control"
                  placeholder="Prompt Name"
                  onBlur={(e) => {
                    if (e.target.value) {
                      promptNameCheckFunction(e.target.value);
                    } else {
                      setNameError(false);
                    }
                  }}
                />
                {nameError && <span className="text-danger">This Name is not Available</span>}
              </div>

              <div className="col-md-4">
                {ToolTipLocalFunction(
                  <>
                    Private Prompts are only accessible to you. Public prompts can be discovered and
                    used by others on the platform.
                  </>,
                  <>Prompt Type</>
                )}

                <ToggleSwitch
                  value={!prompt.is_prompt_type_private}
                  onToggle={() =>
                    handleChange('is_prompt_type_private', !prompt.is_prompt_type_private)
                  }
                  labels={['Private', 'Public']}
                />
              </div>
              <div className="col-md-4">
                {ToolTipLocalFunction(
                  <>
                    <span>
                      <b>Single-turn: </b>Each query or message is handled independently. The system
                      does not keep history of past messages.
                    </span>
                    <br /> <br />
                    <span>
                      <b>Multi-turn: </b>Multi-turn prompts are associated with question and answer
                      pairs.
                    </span>
                  </>,
                  <>Interaction Type</>
                )}

                <ToggleSwitch
                  value={!prompt.is_interaction_type_single}
                  onToggle={() =>
                    handleChange('is_interaction_type_single', !prompt.is_interaction_type_single)
                  }
                  labels={['Single Turn', 'Multi Turn']}
                />
              </div>
            </div>

            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <SearchableDropDownComponent
                  fieldName="LLM Tested With"
                  isMandatory={true}
                  options={ModelOptions}
                  paramName={'model'}
                  value={prompt.llmTestedWith}
                  handleSelection={(a, b) => handleChange('llmTestedWith', b)}
                />
              </div>
              <div className="col-md-4">
                <SearchableDropDownComponent
                  fieldName=" Business Category"
                  isMandatory={true}
                  value={prompt.businessGroup}
                  options={businessCategories?.data || []}
                  paramName={'businessGroup'}
                  handleSelection={(a, b) => {
                    handleChange('businessGroup', b);
                  }}
                />
              </div>
            </div>

            <div className="mb-4">
              {ToolTipLocalFunction(
                <>Write a short summary of what this prompt does and when it should be used.</>,
                <>
                  Prompt Description <span className="text-danger">*</span>
                </>
              )}
              <textarea
                value={prompt.promptDescription}
                onChange={(e) => handleChange('promptDescription', e.target.value)}
                className="form-control addPromptTextarea"
                rows={4}
                placeholder={'Provide a brief description of the prompt'}></textarea>
            </div>

            <div className="mb-4">
              {ToolTipLocalFunction(
                <>
                  Define the core instructions, role, and behavior that the model must follow before
                  responding to the user.
                </>,
                <>
                  System Prompt <span className="text-danger">*</span>
                </>
              )}
              <textarea
                value={prompt.systemPrompt}
                disabled={isSuggestionLoading}
                onChange={(e) => {
                  if (e.target.value) {
                    !manualSystemPrompt && setManualSystemPrompt(true);
                  } else if (!e.target.value) {
                    setManualSystemPrompt(false);
                  }
                  handleChange('systemPrompt', e.target.value);
                }}
                className="form-control addPromptTextarea"
                style={{ height: '150px' }}
                rows={4}
                placeholder={
                  isSuggestionLoading
                    ? 'Generating suggestions for you...'
                    : 'Provide a system prompt'
                }></textarea>
            </div>

            {!prompt.is_interaction_type_single && (
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  {ToolTipLocalFunction(
                    <span>
                      Role defines the persona and helps structure multi-turn conversations.
                      <br /> Message:Instructions associated with the given role. This guides the
                      behavior or response expected from the role
                    </span>,
                    <>
                      {' '}
                      Roles <span className="text-danger">*</span>
                    </>
                  )}

                  <button
                    type="button"
                    className="btn addVaribaleBtn"
                    onClick={() =>
                      setRoles((prev) => [
                        ...prev,
                        { id: Date.now().toString(), role: '', message: '' }
                      ])
                    }>
                    <i className="bi bi-plus me-1"></i>
                    Add Roles
                  </button>
                </div>
                <div
                  style={{
                    border: '1px solid rgba(131, 143, 180, 1)',
                    borderRadius: 8,
                    padding: 16
                  }}>
                  {roles.map((r, index) => (
                    <RoleField
                      key={r?.id + index}
                      role={r}
                      onChange={updateRole}
                      onRemove={(id) => setRoles((prev) => prev.filter((x) => x.id !== id))}
                      canRemove={roles.length > 2}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                {ToolTipLocalFunction(
                  <span>
                    Variable Name: Specify a unique name for the variable. This will be used as a
                    placeholder in your prompt.
                    <br /> Default Value: Provide a default value for the variable. This will be
                    used if no custom input is provided.
                    <br /> Description: Briefly describe the purpose of this variable to help users
                    understand its context.
                    <br /> Add Variable: Click to add another variable to your prompt configuration.
                  </span>,
                  <> Variables</>
                )}

                <button
                  type="button"
                  className="btn addVaribaleBtn"
                  onClick={() =>
                    setVariables((prev) => [
                      ...prev,
                      { id: Date.now().toString(), name: '', defaultValue: '', description: '' }
                    ])
                  }>
                  <i className="bi bi-plus me-1"></i>
                  Add Variable
                </button>
              </div>
              <div
                style={{
                  border: '1px solid rgba(131, 143, 180, 1)',
                  borderRadius: 8,
                  padding: 16
                }}>
                {variables.map((v) => (
                  <VariableField
                    key={v.id}
                    variable={v}
                    onChange={updateVariable}
                    onRemove={(id) => setVariables((prev) => prev.filter((x) => x.id !== id))}
                    canRemove={variables.length > 1}
                  />
                ))}
              </div>
            </div>
            {prompt?.is_interaction_type_single && (
              <div className="mb-4">
                <label className="form-label"></label>

                {ToolTipLocalFunction(
                  <>
                    Write the main instruction the model should follow for this request. This will
                    be processed in a single input–output interaction.
                  </>,
                  <>
                    Prompt <span className="text-danger">*</span>
                  </>
                )}

                <textarea
                  value={prompt.usageExample}
                  onChange={(e) => handleChange('usageExample', e.target.value)}
                  className="form-control"
                  style={{ height: '150px' }}
                  rows={4}
                  placeholder="Please write your prompt"></textarea>
              </div>
            )}
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 mt-4 mb-4">
          <button onClick={handleReset} className="btn resetBtn me-4">
            <img src={resetIcon} alt="reset" /> Reset
          </button>

          <UnileverButton
            buttonAction={promptId ? handleEdit : handleSubmit}
            disabled={!isButtonActive() || isDataLoading}>
            <img src={tickIcon} alt="tick" /> {isDataLoading ? 'Loading..' : 'Submit Prompt'}
          </UnileverButton>
        </div>
      </div>
    </div>
  );
};

export default AddPrompt;
